import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

type GroupByMode = 'day' | 'week' | 'month'

interface PeriodBucket {
  key: string
  label: string
  totalOrders: number
  totalAmountCents: number
  averageTicketCents: number
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const parseOrderDate = (rawDate?: string | null): Date | null => {
  if (!rawDate) {
    return null
  }

  const parsed = new Date(rawDate)
  if (!Number.isNaN(parsed.getTime())) {
    return parsed
  }

  return null
}

const getOrderTotalCents = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value)
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return Math.round(parsed)
    }
  }

  return 0
}

const getWeekInfo = (date: Date): { year: number; week: number } => {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNumber = utcDate.getUTCDay() || 7
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNumber)
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1))
  const week = Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)

  return {
    year: utcDate.getUTCFullYear(),
    week,
  }
}

const getPeriodFromDate = (date: Date, groupBy: GroupByMode): { key: string; label: string } => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  if (groupBy === 'day') {
    return {
      key: `${year}-${month}-${day}`,
      label: `${day}/${month}/${year}`,
    }
  }

  if (groupBy === 'month') {
    return {
      key: `${year}-${month}`,
      label: `${month}/${year}`,
    }
  }

  const weekInfo = getWeekInfo(date)
  const weekLabel = String(weekInfo.week).padStart(2, '0')

  return {
    key: `${weekInfo.year}-W${weekLabel}`,
    label: `Semana ${weekLabel}/${weekInfo.year}`,
  }
}

const buildPeriodBuckets = (
  report: SellerTicketAverageReport | null,
  groupBy: GroupByMode
): PeriodBucket[] => {
  if (!report?.items.length) {
    return []
  }

  const grouped = new Map<string, { label: string; totalOrders: number; totalAmountCents: number }>()

  for (const order of report.items) {
    const date = parseOrderDate(order.created)
    if (!date) {
      continue
    }

    const period = getPeriodFromDate(date, groupBy)
    const current = grouped.get(period.key) || {
      label: period.label,
      totalOrders: 0,
      totalAmountCents: 0,
    }

    current.totalOrders += 1
    current.totalAmountCents += getOrderTotalCents(order.total)
    grouped.set(period.key, current)
  }

  return Array.from(grouped.entries())
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => ({
      key,
      label: value.label,
      totalOrders: value.totalOrders,
      totalAmountCents: value.totalAmountCents,
      averageTicketCents:
        value.totalOrders > 0 ? Math.round(value.totalAmountCents / value.totalOrders) : 0,
    }))
}

const exportToCSV = (filename: string, buckets: PeriodBucket[], groupBy: GroupByMode, report: SellerTicketAverageReport | null): void => {
  let csv = 'sep=;\n'
  csv += `Agrupamento;${groupBy}\n`
  csv += 'Periodo;Pedidos;Faturamento;Ticket medio\n'

  buckets.forEach((bucket) => {
    csv += `"${bucket.label}";${bucket.totalOrders};${bucket.totalAmountCents / 100};${bucket.averageTicketCents / 100}\n`
  })

  csv += '\nRESUMO\n'
  csv += 'Pedidos totais;Ticket médio geral\n'
  csv += `${report?.summary.totalOrders || 0};${(report?.summary.averageTicketCents || 0) / 100}\n`

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, buckets: PeriodBucket[], groupBy: GroupByMode, report: SellerTicketAverageReport | null): void => {
  const workbook = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(
    buckets.map((bucket) => ({
      Periodo: bucket.label,
      Pedidos: bucket.totalOrders,
      Faturamento: bucket.totalAmountCents / 100,
      'Ticket medio': bucket.averageTicketCents / 100,
      Agrupamento: groupBy,
    }))
  )
  const summarySheet = XLSX.utils.json_to_sheet([
    {
      'Pedidos totais': report?.summary.totalOrders || 0,
      'Ticket médio geral': (report?.summary.averageTicketCents || 0) / 100,
      Agrupamento: groupBy,
    },
  ])

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo')
  XLSX.utils.book_append_sheet(workbook, sheet, 'Por Periodo')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, buckets: PeriodBucket[], groupBy: GroupByMode, report: SellerTicketAverageReport | null): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Evolução por Período', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text(`Agrupamento: ${groupBy}`, margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Pedidos totais: ${report?.summary.totalOrders || 0} | Ticket médio geral: ${formatCurrency(report?.summary.averageTicketCents || 0)}`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Período', 'Pedidos', 'Faturamento', 'Ticket médio']],
    body: buckets.map((bucket) => [
      bucket.label,
      bucket.totalOrders,
      formatCurrency(bucket.totalAmountCents),
      formatCurrency(bucket.averageTicketCents),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const PeriodEvolutionReportPage: FC = () => {
  const [filters, setFilters] = useState<SellerTicketAverageFilters>({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
    source: 'all',
  })
  const [groupBy, setGroupBy] = useState<GroupByMode>('day')
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const buckets = useMemo(() => buildPeriodBuckets(report, groupBy), [report, groupBy])

  const totalPeriods = buckets.length
  const bestPeriod = useMemo(() => {
    if (!buckets.length) {
      return null
    }

    return [...buckets].sort((left, right) => right.totalAmountCents - left.totalAmountCents)[0]
  }, [buckets])

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle breadcrumbs={[{ title: 'Evolução por Período', path: '/seller/reports/period-evolution', isActive: true }]}>
        Relatório de Evolução por Período
      </PageTitle>

      <ReportShell
        title='Evolução de Pedidos por Período'
        description='Análise temporal de volume, faturamento e ticket médio por dia, semana ou mês, com filtros de data e origem.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Evolucao-Periodo-${timestamp}.csv`, buckets, groupBy, report)
            }} disabled={loading || buckets.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Evolucao-Periodo-${timestamp}.xlsx`, buckets, groupBy, report)
            }} disabled={loading || buckets.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Evolucao-Periodo-${timestamp}.pdf`, buckets, groupBy, report)
            }} disabled={loading || buckets.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label fw-semibold'>Agrupar por</label>
              <select
                className='form-select'
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as GroupByMode)}
              >
                <option value='day'>Dia</option>
                <option value='week'>Semana</option>
                <option value='month'>Mês</option>
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Períodos com movimento</div>
                <div className='fs-2hx fw-bold text-primary'>{totalPeriods.toLocaleString('pt-BR')}</div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Pedidos totais</div>
                <div className='fs-2hx fw-bold text-info'>
                  {(report?.summary.totalOrders || 0).toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Melhor período por receita</div>
                <div className='fs-3 fw-bold text-dark'>{bestPeriod ? bestPeriod.label : 'Sem dados'}</div>
                <div className='text-gray-700 fw-semibold mt-2'>
                  {bestPeriod ? formatCurrency(bestPeriod.totalAmountCents) : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Evolução temporal</span>
              <span className='text-gray-600 mt-1'>Quantidade, faturamento, ticket médio e participação por período.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : buckets.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Período</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Participação</th>
                      <th className='text-end'>Faturamento</th>
                      <th className='text-end'>Ticket médio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buckets.map((bucket) => {
                      const participation =
                        (report?.summary.totalOrders || 0) > 0
                          ? (bucket.totalOrders / (report?.summary.totalOrders || 1)) * 100
                          : 0

                      return (
                        <tr key={bucket.key}>
                          <td>
                            <div className='fw-bold text-dark'>{bucket.label}</div>
                          </td>
                          <td className='text-end fw-bold'>{bucket.totalOrders}</td>
                          <td className='text-end'>{percentFormatter.format(participation)}%</td>
                          <td className='text-end fw-bold text-success'>
                            {formatCurrency(bucket.totalAmountCents)}
                          </td>
                          <td className='text-end'>{formatCurrency(bucket.averageTicketCents)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum pedido encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { PeriodEvolutionReportPage }